import {Component, computed, effect, inject, OnInit, untracked} from '@angular/core';
import {Button} from 'primeng/button';
import {IconBtnComponent} from '../../icon-b/icon-btn.component';
import {Card} from 'primeng/card';
import {Checkbox, CheckboxChangeEvent} from 'primeng/checkbox';
import {FormsModule} from '@angular/forms';
import {InputText} from 'primeng/inputtext';
import {CreateTaBaseComponent} from '../create-ta-base-component.directive';
import {AutoComplete, AutoCompleteCompleteEvent} from 'primeng/autocomplete';
import {DialogService, DynamicDialogRef} from 'primeng/dynamicdialog';
import {DeductionAuthModalComponent} from '../deduction-auth-modal/deduction-auth-modal.component';
import {TravellerDetails, UserType} from '../../../../api';
import {DatePipe} from '@angular/common';
import {getUserTypeSuffix} from '../../../../util/general.utils';
import {isDayTripItinerary, latestItinerariesForSubmission} from '../../../../util/itinerary.utils';
import {TaToastComponent} from '../../ta-toast/ta-toast.component';
import {fuzzySearch} from '../../../../util/search.utils';

@Component({
  selector: 'create-ta-travellers',
  imports: [
    Button,
    IconBtnComponent,
    Card,
    Checkbox,
    FormsModule,
    InputText,
    AutoComplete,
    DatePipe,
    TaToastComponent,
  ],
  providers: [
    DialogService
  ],
  templateUrl: './create-ta-travellers.component.html',
  styleUrl: './create-ta-travellers.component.scss'
})
export class CreateTaTravellersComponent extends CreateTaBaseComponent implements OnInit {
  //Local Variables
  emptyTravellerTemplate: TravellerDetails = {
    details: undefined,
    isSelfAcquit: false,
    authorisedAdjustmentDate: null,
  }
  emptyTraveller: TravellerDetails = {...this.emptyTravellerTemplate};
  availableTravellers = computed<TravellerDetails[]>(() => (
    this.store.availableUsers().map(user => ({
      details: user,
      authorisedAdjustmentDate: null,
      isSelfAcquit: false
    }))
  ));
  filteredTravellers: TravellerDetails[] = [];
  ref: DynamicDialogRef | null | undefined;
  // A booking-officer TA: raised on someone else's behalf. That is how the client identifies one.
  isDelegateTa = computed<boolean>(() => !!this.taSubmission()?.delegate);
  protected readonly UserType = UserType;
  protected readonly getUserTypeSuffix = getUserTypeSuffix;
  // Pre-payment is not offered on day trips: an existing single-day itinerary disables it. No
  // itinerary yet leaves it enabled.
  isDayTrip = computed<boolean>(() =>
    latestItinerariesForSubmission(this.taSubmissionDatasource.allItineraries(), this.taSubmission()?.id)
      .some(isDayTripItinerary));
  private taId = computed(() => this.taSubmission()?.id);
  readonlyNonStaffTravellers = computed(() => {
    if (!!this.taSubmission()?.nonClcTravellers) {
      return this.taSubmission()!.nonClcTravellers!.filter(t => !!t);
    }
    return [];
  })

  constructor(
    private dialogService: DialogService,
  ) {
    super();

    // Read-only fetch (getOrCreate would make an itinerary); re-runs only when the TA changes.
    effect(() => {
      if (this.taId()) {
        untracked(() => this.taSubmissionDatasource.getAllItineraries());
      }
    });

    effect(() => {
      let travellers = this.taSubmission()?.travellers ?? [];
      this.addAcquit(travellers);
      this.removePrepayment(travellers);
    });
  }

  override ngOnInit(): void {
    super.ngOnInit();

    this.taSearchDatasource.loadTravellerOptions();
  }

  modalConfig(traveller: TravellerDetails) {
    return {
      // Header is the same for both versions; the modal picks the delegate or the standard
      // document itself, from whether the TA has a booking officer.
      header: 'EMPLOYEE DEDUCTION AUTHORISATION – TRAVEL ALLOWANCE PRE-PAYMENT',
      inputValues: {
        traveller: traveller,
      },
      contentStyle: {
        overflow: 'auto'
      },
      modal: true,
      width: '50%',
    };
  }

  deductionAuthCheck(event: PointerEvent, traveller: TravellerDetails): void {

    // @ts-ignore
    const checked = event.target?.checked;
    event.preventDefault();
    event.stopPropagation();


    //Check if changing prepayment to true
    if (checked) {
      this.ref = this.dialogService.open(DeductionAuthModalComponent, this.modalConfig(traveller));
      this.ref?.onClose.subscribe((data) => {
        // console.log('deduction modal closed', data);
        if (!!data && data?.authorisation) {
          traveller.authorisedAdjustmentDate = new Date().toISOString()
          this.updateTraveller(traveller);
        }
      });
    } else {
      traveller.authorisedAdjustmentDate = null;
      this.updateTraveller(traveller);
    }
  }

  // authorisedByUserId is only a GUID; the one name we can resolve without another lookup is the
  // TA's delegate, which is who stamps it on a delegate TA.
  authorityChipLabel(traveller: TravellerDetails): string {
    const authorisedBy = traveller.authorisedByUserId;
    if (!authorisedBy || authorisedBy === traveller.details?.id) {
      return 'Authority Provided';
    }
    const delegate = this.taSubmission()?.delegate;
    return delegate?.id === authorisedBy && !!delegate?.name
      ? `Authority Provided by ${delegate.name} on`
      : 'Authority Provided on behalf of traveller on';
  }

  removePrepayment(travellers: TravellerDetails[]): void {
    if (travellers.length > 1 && travellers?.some(t => !!t.authorisedAdjustmentDate)) {
      let newTravellers = travellers.map((traveller) => ({...traveller, authorisedAdjustmentDate: null}));
      this.taSubmissionDatasource.saveTaTravellers('travellers', newTravellers);
    }
  }

  addAcquit(travellers: TravellerDetails[]): void {
    // On a delegate TA the booking officer acquits every travel portion by default, so never
    // force a traveller's acquittal flag on. They can still be opted in via the checkbox.
    if (this.isDelegateTa()) {
      return;
    }
    if (travellers.length === 1) {
      const desired = travellers.at(0)!.details?.userType !== UserType.Casual;
      if (!!travellers.at(0)!.isSelfAcquit !== desired) {
        let newTravellers = travellers.map((traveller) => ({...traveller, isSelfAcquit: desired}));
        this.taSubmissionDatasource.saveTaTravellers('travellers', newTravellers);
      }
    }
  }

  updateTravellerAcquittal(acquit: boolean, traveller: TravellerDetails): void {
    traveller.isSelfAcquit = acquit;
    this.updateTraveller(traveller);
  }

  updateTraveller(traveller: TravellerDetails): void {
    this.taSubmissionDatasource.saveTaTraveller(traveller);
  }

  addStaffTraveller(): void {
    let travellers = this.taSubmission()?.travellers ?? [];
    this.taSubmissionDatasource.saveTaTravellers('travellers', [...travellers, this.emptyTraveller]);
    if (travellers.length === 0 && this.emptyTraveller.details?.userType !== UserType.Casual) {
      this.addPrimaryRole(this.emptyTraveller);
    }
    this.emptyTraveller = {...this.emptyTravellerTemplate};
  }

  // Flight-only TAs have one traveller; the server makes them primary and re-derives the approver.
  replaceFlightOnlyTraveller(traveller: TravellerDetails | undefined): void {
    if (!traveller?.details?.id) {
      return;
    }
    this.taSubmissionDatasource.saveTaTravellers('travellers', [{...traveller, isSelfAcquit: !this.isDelegateTa()}]);
    this.emptyTraveller = {...this.emptyTravellerTemplate};
  }

  removeStaffTraveller(traveller: TravellerDetails): void {
    let newTravellers: TravellerDetails[] = this.taSubmission()?.travellers?.filter(t => t !== traveller) ?? [];
    this.taSubmissionDatasource.saveTaTravellers('travellers', [...newTravellers]);

    // The server re-derives the primary when the removed traveller held it: a casual on an
    // all-casual delegate TA, null otherwise. Only a remaining staff traveller needs promoting here.
    if (traveller.details?.id === this.taSubmission()!.primaryTraveller?.details?.id) {
      const newPrimary = newTravellers.find(t => t.details?.userType !== UserType.Casual);
      if (newPrimary) {
        this.taSubmissionDatasource.saveTaFieldString('primaryTraveller', newPrimary.details?.id?.toString(), {...newPrimary});
      }
    }
  }

  addPrimaryRole(traveller: TravellerDetails): void {
    const primaryId  = traveller.details!.id;
    // Promoting someone to primary must not silently opt them into acquitting their own travel
    // on a delegate TA - the booking officer acquits unless the delegate ticks the box.
    if (!this.isDelegateTa()) {
      this.taSubmissionDatasource.saveTaTraveller({...traveller, isSelfAcquit: true});
    }
    this.taSubmissionDatasource.saveTaFieldString('primaryTraveller', primaryId, traveller);
  }

  addNonStaffTraveller(): void {
    if (!!this.taSubmission()!.nonClcTravellers && this.taSubmission()!.nonClcTravellers!.length > 0) {
      this.saveNonClcTraveller([...this.taSubmission()!.nonClcTravellers!, '']);
    } else {
      this.saveNonClcTraveller(['']);
    }
  }

  updateNonStaffTraveller(event: Event, index: number): void {
    const value = (event.target as HTMLInputElement).value;
    let newNonClcTravellers: string[] = this.taSubmission()!.nonClcTravellers?.map((tt,ii) => {
      if (ii === index) {
        return value
      }
      return tt
    }) ?? [];
    this.saveNonClcTraveller([...newNonClcTravellers]);
  }

  removeNonStaffTraveller(travellerIndex: number): void {
    let newNonClcTravellers: string[] = this.taSubmission()!.nonClcTravellers?.filter((t,index) => (index !== travellerIndex)) ?? [];
    this.saveNonClcTraveller([...newNonClcTravellers]);
  }

  preventComma(event: KeyboardEvent): void {
    if (event.key === ',') {
      event.preventDefault(); // stops the comma from being entered
    }
  }

  saveNonClcTraveller(nonClcTravellers: string[]): void {
    const nonClcTravellersSanitized = nonClcTravellers.map((traveller) => traveller.replace(/,/g, ''))
    const commaString = nonClcTravellersSanitized?.join(',');
    this.taSubmissionDatasource.saveTaFieldString('nonClcTravellers', commaString, nonClcTravellersSanitized);
  }

  filterTravellers(event: AutoCompleteCompleteEvent): void {
    const userIds =  this.taSubmission()!.travellers?.map((traveller) => (traveller.details?.id));
    // Casuals can lead the list on a delegate TA, where they may also hold the primary role; on a
    // self TA the primary is always staff.
    // A flight-only traveller is always the primary, so never a casual.
    const isAddingPrimary = this.isFlightOnly()
      || (!this.isDelegateTa() && (this.taSubmission()?.travellers?.length ?? 0) === 0);
    // A booking officer is not travelling on the TA they raise, so neither the TA's delegate nor
    // the person editing it can be picked as a traveller on a delegate TA.
    const excludedIds = this.isDelegateTa()
      ? [this.taSubmission()?.delegate?.id, this.store.user()?.id].filter(id => !!id)
      : [];
    const eligible = this.availableTravellers().filter(traveller =>
      !userIds?.includes(traveller.details?.id) &&
      !excludedIds.includes(traveller.details?.id) &&
      (!isAddingPrimary || traveller.details?.userType !== UserType.Casual)
    );
    this.filteredTravellers = fuzzySearch(eligible, event.query, t => this.travellerSearchText(t));
  }

  // Name only: email and initials are deliberately not searchable.
  private travellerSearchText(traveller: TravellerDetails): string {
    const details = traveller.details;
    return [details?.firstName, details?.surname].filter(Boolean).join(' ') || details?.name || '';
  }

  getTravellersFullName(traveller: TravellerDetails): string {
    if (traveller.details != null) {
      if (traveller.details?.firstName != null) {
        return traveller.details?.firstName + " " + (traveller.details?.surname ?? '');
      }
      if (traveller.details.name != null){
        return traveller.details.name;
      }
      return traveller.details!.email!;
    }
    return '';
  }
}
